import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import ErrorIcon from '@mui/icons-material/ErrorOutline';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useHostingLicenseInfo } from '@newfold/huapi-js';
import { HostingLicenseModel } from '@newfold/huapi-js/src/index.schemas';

import useAccount from '~/hooks/useAccount';
import { PublicImageItem } from '~/Nova/scenes/HostingList/utils/types';
import EmptySectionCard from '~/Nova/scenes/Site/components/EmptySectionCard';

import AppLogo from '../../../components/AppLogo';
import { SUPPORTED_LICENSE_SUBTYPES } from '../../../utils/constants';
import { getLogoConfig } from '../../../utils/imageLogos';
import LicenseCard from './LicenseCard';

interface LicensesProps {
  osAdvancedImages?: PublicImageItem[];
}

const Licenses = ({ osAdvancedImages = [] }: LicensesProps) => {
  const { t } = useTranslation('selfManagedHosting', {
    keyPrefix: 'licenses',
  });
  const { id: hostingId } = useAccount();

  const {
    data: availableLicenses,
    refetch: refetchLicenses,
    isLoading,
    isError,
  } = useHostingLicenseInfo(
    hostingId,
    {
      subtype: SUPPORTED_LICENSE_SUBTYPES,
    },
    {
      query: {
        enabled: !!hostingId,
        select: (response) => response?.data?.licenses ?? [],
      },
    },
  );

  const licenseBySubtypeMap = useMemo(() => {
    const map = new Map<string, HostingLicenseModel>();
    for (const license of availableLicenses ?? []) {
      if (license.subtype) {
        map.set(license.subtype?.toLowerCase() ?? '', license);
      }
    }
    return map;
  }, [availableLicenses]);

  const handleRetry = useCallback(() => {
    void refetchLicenses();
  }, [refetchLicenses]);

  const shouldShowErrorState = isError && !isLoading && !availableLicenses;

  if (shouldShowErrorState) {
    return (
      <Card>
        <EmptySectionCard>
          <IconButton disableRipple>
            <ErrorIcon sx={{ fontSize: 40 }} color="error" />
          </IconButton>
          <Stack spacing={2} alignItems="center" mt={1}>
            <Typography variant="body1">
              {t('error.fetchingLicense')}
            </Typography>
            <Button variant="outlined" onClick={handleRetry}>
              {t('error.tryAgain')}
            </Button>
          </Stack>
        </EmptySectionCard>
      </Card>
    );
  }

  return SUPPORTED_LICENSE_SUBTYPES.map((appName) => {
    const appLogoResult = getLogoConfig(appName);
    const { logo: appLogo = '', logoAlt: appLogoAlt = '' } =
      appLogoResult || {};

    const appImageMeta = osAdvancedImages.find(
      (img) =>
        img.app === appName ||
        img.software?.toLowerCase() === appName.toLowerCase(),
    );

    const logoConfig = {
      key: appName,
      name: appImageMeta?.title ?? appName,
      logo: appLogo,
      logoAlt: appLogoAlt,
      size: {
        small: { width: 25, height: 25 },
        large: { width: 41, height: 24 },
      },
    };

    return (
      <LicenseCard
        key={appName}
        license={licenseBySubtypeMap.get(appName.toLowerCase()) ?? null}
        isLoading={isLoading}
        appName={appName}
        appLogo={<AppLogo logoConfig={logoConfig} spacing={1} />}
      />
    );
  });
};

export default Licenses;
